import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { type LoginResponse } from '../api/auth'
import {
  partnersApi,
  type Partner,
  type PartnerLedger,
  type PartnerLedgerMovement,
  type PartnerMovementBody,
  type PartnerOverview,
  PARTNER_SECTIONS,
} from '../api/partners'
import { expensesApi, financialAccountsApi, type Expense, type FinancialAccountOption } from '../api/finance-ext'
import {
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  DataTable,
  DataTableEmptyRow,
  Dialog,
  FilterBar,
  FormGrid,
  FormSection,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatGrid,
  StatusPill,
  TextField,
} from '../components/ui'
import { PartnerPositionsPanel } from '../components/PartnerPositionsPanel'
import { useFeatureFlag } from '../lib/featureFlags'

/**
 * F-059 (TASK-0313, REQ-130-001 / REQ-170-001 / REQ-171-001 / REQ-172-001) — conto corrente dei soci.
 *
 * <b>Il saldo non è mai calcolato qui.</b> Saldo, totali, "somme attribuite/liquidate" e saldo
 * progressivo arrivano dal server (somma dei movimenti, `PartnerLedgerCalculator`); questa pagina li
 * mostra soltanto. Convenzione: saldo positivo = la società deve al socio.
 *
 * <b>Chi vede cosa.</b> L'Admin sceglie un socio qualsiasi e può scrivere (nuovo movimento, approva,
 * storna, compensa, spesa pagata dal socio, collega utente). Un utente collegato a un socio vede solo
 * il proprio conto (`/api/partners/me`), in sola lettura. Il gating è anche lato server: qui si
 * nascondono soltanto i pulsanti.
 *
 * <b>Nessuna modifica o cancellazione.</b> Un movimento sbagliato si corregge con "Storna" (movimento
 * di segno opposto, categoria Rettifica); un errore di storno non si storna a sua volta.
 */

interface Props {
  auth: LoginResponse
}

function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

function formatPct(n: number | null | undefined): string {
  return n == null ? '—' : (n * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 }) + '%'
}

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(s?: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('it-IT')
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function balanceClass(n: number): string {
  return n > 0 ? 'text-success' : n < 0 ? 'text-danger' : 'text-muted'
}

function balanceNote(n: number): string {
  if (n > 0) return 'La società deve al socio'
  if (n < 0) return 'Il socio deve alla società'
  return 'In pari'
}

interface MovementForm {
  movementDate: string
  category: string
  amount: string
  effect: string
  description: string
  documentReference: string
  sourceAccountId: string
  destinationAccountId: string
  costCentre: string
  notes: string
}

const EMPTY_MOVEMENT: MovementForm = {
  movementDate: today(),
  category: '',
  amount: '',
  effect: '',
  description: '',
  documentReference: '',
  sourceAccountId: '',
  destinationAccountId: '',
  costCentre: '',
  notes: '',
}

export function PartnersPage({ auth }: Props) {
  const admin = isAdmin(auth)
  const partnersOn = useFeatureFlag('finanza.soci_da_conti')

  const [partners, setPartners] = useState<Partner[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [meChecked, setMeChecked] = useState(false)
  const [error, setError] = useState('')

  const [ledger, setLedger] = useState<PartnerLedger | null>(null)
  const [loading, setLoading] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const [accounts, setAccounts] = useState<FinancialAccountOption[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  // F-069: panoramica del socio collegato (saldo + sezioni abilitate dall'Admin). Solo per chi non e' Admin.
  const [overview, setOverview] = useState<PartnerOverview | null>(null)

  // Selezione iniziale: Admin → elenco soci; altri → il proprio socio (se collegato).
  useEffect(() => {
    let cancelled = false
    async function init() {
      if (admin) {
        try {
          const list = await partnersApi.list()
          if (cancelled) return
          setPartners(list)
          if (list.length > 0) setSelectedId(list[0]!.id)
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : 'Errore durante il caricamento dei soci')
        }
        financialAccountsApi.list().then(setAccounts).catch(() => setAccounts([]))
        expensesApi.list({ page: 1, size: 200 }).then(r => setExpenses(r.items)).catch(() => setExpenses([]))
      } else {
        const me = await partnersApi.me()
        if (cancelled) return
        if (me) setSelectedId(me.id)
      }
      if (!cancelled) setMeChecked(true)
    }
    void init()
    return () => { cancelled = true }
  }, [admin])

  const loadLedger = useCallback(async () => {
    if (selectedId === null) { setLedger(null); return }
    setLoading(true)
    setError('')
    try {
      setLedger(await partnersApi.ledger(selectedId, from || undefined, to || undefined))
    } catch (e) {
      setLedger(null)
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento del conto')
    } finally {
      setLoading(false)
    }
  }, [selectedId, from, to])

  useEffect(() => { void loadLedger() }, [loadLedger])

  useEffect(() => {
    if (admin || selectedId === null) { setOverview(null); return }
    let cancelled = false
    void partnersApi.overview().then(o => { if (!cancelled) setOverview(o) })
    return () => { cancelled = true }
  }, [admin, selectedId])

  return (
    <Page>
      <PageHeader
        title={admin ? 'Conto soci' : 'Il mio conto socio'}
        icon="handshake"
      />
      {partnersOn && <PartnerPositionsPanel />}

      {(admin || selectedId !== null) && (
        <FilterBar>
          {admin && (
            <Select
              label="Socio"
              className="md-u-maxw-320"
              value={selectedId ?? ''}
              onChange={e => setSelectedId(e.target.value ? Number(e.target.value) : null)}
            >
              {partners.length === 0 && <option value="">Nessun socio in anagrafica</option>}
              {partners.map(p => (
                <option key={p.id} value={p.id}>{p.lastName} {p.firstName}</option>
              ))}
            </Select>
          )}
          {selectedId !== null && (
            <>
              <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
              <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </>
          )}
        </FilterBar>
      )}

      {error && <Callout tone="error">{error}</Callout>}

      {!admin && meChecked && selectedId === null && (
        <Callout tone="info">Nessun conto socio è collegato al tuo utente.</Callout>
      )}

      {loading && !ledger && <LoadingState />}

      {overview && <OverviewSections overview={overview} />}

      {ledger && (
        <LedgerView
          ledger={ledger}
          admin={admin}
          partner={partners.find(p => p.id === ledger.partner.id)}
          accounts={accounts}
          expenses={expenses}
          onChanged={() => { void loadLedger() }}
          onPartnerChanged={async () => { setPartners(await partnersApi.list()) }}
        />
      )}
    </Page>
  )
}

/* ── Conto di un socio ───────────────────────────────────────────────────────────────────────── */

function LedgerView({ ledger, admin, partner, accounts, expenses, onChanged, onPartnerChanged }: {
  ledger: PartnerLedger
  admin: boolean
  partner: Partner | undefined
  accounts: FinancialAccountOption[]
  expenses: Expense[]
  onChanged: () => void
  onPartnerChanged: () => Promise<void>
}) {
  const s = ledger.summary
  const accountName = (id?: number | null) => (id ? accounts.find(a => a.id === id)?.name ?? `#${id}` : '')

  const [showForm, setShowForm] = useState(false)
  const [showExpensePaid, setShowExpensePaid] = useState(false)
  const [settleTarget, setSettleTarget] = useState<PartnerLedgerMovement | null>(null)
  const [reverseTarget, setReverseTarget] = useState<PartnerLedgerMovement | null>(null)
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  async function approve(m: PartnerLedgerMovement) {
    setBusy(m.id)
    setRowError(null)
    try {
      await partnersApi.approve(ledger.partner.id, m.id)
      onChanged()
    } catch (e) {
      // 409 quando l'approvatore è chi ha creato il movimento (quattro occhi): messaggio del server.
      setRowError({ id: m.id, message: e instanceof Error ? e.message : 'Errore durante l\'approvazione' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {/* Riepilogo (REQ-170-001) */}
      <StatGrid>
        <SummaryCard label="Saldo socio / società" value={formatEur(s.balance)} className={balanceClass(s.balance)} note={balanceNote(s.balance)} />
        <SummaryCard label="Totale a credito del socio" value={formatEur(s.totalCredits)} />
        <SummaryCard label="Totale a debito del socio" value={formatEur(s.totalDebits)} />
        <SummaryCard label="Somme attribuite" value={formatEur(s.attributedAmounts)} note="Quote di utile" />
        <SummaryCard label="Somme liquidate" value={formatEur(s.liquidatedAmounts)} note="Distribuzioni e anticipi" />
        <SummaryCard label="Da approvare" value={String(s.pendingApprovalCount)} note="Movimenti senza approvazione" />
      </StatGrid>

      {(ledger.partner.participationPercentage != null || ledger.partner.distributionPercentage != null) && (
        <p className="text-muted md-typescale-body-small">
          Quota di partecipazione: {ledger.partner.participationPercentage ?? '—'}% · quota di distribuzione: {ledger.partner.distributionPercentage ?? '—'}%
        </p>
      )}

      <Card padding="none">
        <CardHeader title="Totali per categoria" />
          <DataTable variant="plain">
            <thead>
              <tr><th className="ps-3">Categoria</th><th className="text-end">Movimenti</th><th className="text-end pe-3">Importo</th></tr>
            </thead>
            <tbody>
              {s.byCategory.length === 0 && <DataTableEmptyRow colSpan={3} title="Nessun movimento" />}
              {s.byCategory.map(c => (
                <tr key={c.category}>
                  <td className="ps-3">{c.label}</td>
                  <td className="text-end md-num">{c.count}</td>
                  <td className="text-end pe-3 text-nowrap md-num">{formatEur(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>

      {/* Azioni */}
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div className="flex-grow-1" />
        {admin && (
          <>
            <LinkUserControl ledgerPartnerId={ledger.partner.id} currentUserId={partner?.userId ?? ledger.partner.userId ?? null} onChanged={onPartnerChanged} />
            <DisclosureControl partnerId={ledger.partner.id} current={partner?.disclosedSections ?? ''} onChanged={onPartnerChanged} />
            <Button variant="outlined" onClick={() => setShowExpensePaid(true)}>
              Spesa pagata dal socio
            </Button>
            <Button variant="filled" onClick={() => setShowForm(true)}>
              Nuovo movimento
            </Button>
          </>
        )}
      </div>

      {/* Movimenti con saldo progressivo */}
      <Card padding="none">
          <DataTable variant="plain" stickyHeader>
            <thead>
              <tr>
                <th className="ps-3">Data</th>
                <th>Categoria</th>
                <th>Causale</th>
                <th className="text-end">Importo</th>
                <th className="text-end">Saldo progressivo</th>
                <th>Approvazione</th>
                {admin && <th className="text-end pe-3">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {ledger.movements.length === 0 && (
                <DataTableEmptyRow colSpan={admin ? 7 : 6} title="Nessun movimento nel periodo" />
              )}
              {ledger.movements.map(m => {
                const canSettle = (m.category === 'SpesaAnticipata' || m.category === 'IncassoPerContoAzienda')
                  && !m.settledByMovementId && !m.reversedByMovementId
                const canReverse = !m.reversesMovementId && !m.reversedByMovementId
                return (
                  <tr key={m.id}>
                    <td className="ps-3 text-nowrap">{formatDate(m.movementDate)}</td>
                    <td>
                      {m.categoryLabel}
                      {m.settledByMovementId && <StatusPill tone="success" className="ms-1">Compensato</StatusPill>}
                      {m.reversedByMovementId && <StatusPill tone="neutral" className="ms-1">Stornato</StatusPill>}
                      {m.reversesMovementId && <StatusPill tone="neutral" className="ms-1">Storno di #{m.reversesMovementId}</StatusPill>}
                      {m.linkedMovementId && <StatusPill tone="info" className="ms-1">Compensa #{m.linkedMovementId}</StatusPill>}
                    </td>
                    <td>
                      {m.description}
                      <div className="text-muted md-typescale-body-small">
                        {[m.documentReference, m.expenseId ? `Spesa #${m.expenseId}` : '', accountName(m.sourceAccountId), accountName(m.destinationAccountId), m.costCentre]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className={`text-end text-nowrap md-num ${m.effect > 0 ? 'text-success' : 'text-danger'}`}>
                      {m.effect > 0 ? '+' : '−'} {formatEur(m.amount)}
                    </td>
                    <td className={`text-end text-nowrap md-num md-typescale-emphasized-body-medium ${balanceClass(m.runningBalance)}`}>{formatEur(m.runningBalance)}</td>
                    <td className="text-nowrap">
                      {m.approvedAt
                        ? <StatusPill tone="success">Approvato {formatDate(m.approvedAt)}</StatusPill>
                        : <StatusPill tone="warning">Da approvare</StatusPill>}
                    </td>
                    {admin && (
                      <td className="text-end pe-3">
                        <div className="d-flex gap-1 justify-content-end flex-wrap">
                          {!m.approvedAt && (
                            <IconButton icon="check_circle" size="sm" aria-label="Approva" disabled={busy === m.id} onClick={() => void approve(m)} />
                          )}
                          {canSettle && (
                            <IconButton icon="swap_horiz" size="sm" aria-label="Compensa" onClick={() => setSettleTarget(m)} />
                          )}
                          {canReverse && (
                            <IconButton icon="undo" size="sm" aria-label="Storna" onClick={() => setReverseTarget(m)} />
                          )}
                        </div>
                        {rowError?.id === m.id && <div className="text-danger md-typescale-body-small mt-1">{rowError.message}</div>}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
      </Card>

      {showForm && (
        <MovementModal
          partnerId={ledger.partner.id}
          ledger={ledger}
          accounts={accounts}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); onChanged() }}
        />
      )}
      {showExpensePaid && (
        <ExpensePaidModal
          partnerId={ledger.partner.id}
          expenses={expenses}
          accounts={accounts}
          onClose={() => setShowExpensePaid(false)}
          onSaved={() => { setShowExpensePaid(false); onChanged() }}
        />
      )}
      {settleTarget && (
        <SettleModal
          partnerId={ledger.partner.id}
          movement={settleTarget}
          accounts={accounts}
          onClose={() => setSettleTarget(null)}
          onSaved={() => { setSettleTarget(null); onChanged() }}
        />
      )}
      {reverseTarget && (
        <ReverseModal
          partnerId={ledger.partner.id}
          movement={reverseTarget}
          onClose={() => setReverseTarget(null)}
          onSaved={() => { setReverseTarget(null); onChanged() }}
        />
      )}
    </>
  )
}

function SummaryCard({ label, value, note, className }: { label: string; value: string; note?: string; className?: string }) {
  return (
    <StatCard label={label} value={<span className={className ?? ''}>{value}</span>} supporting={note} />
  )
}

/* ── Collegamento utente ─────────────────────────────────────────────────────────────────────── */

function LinkUserControl({ ledgerPartnerId, currentUserId, onChanged }: {
  ledgerPartnerId: number
  currentUserId: number | null
  onChanged: () => Promise<void>
}) {
  const [value, setValue] = useState(currentUserId ? String(currentUserId) : '')
  const [message, setMessage] = useState('')

  useEffect(() => { setValue(currentUserId ? String(currentUserId) : ''); setMessage('') }, [ledgerPartnerId, currentUserId])

  async function save() {
    setMessage('')
    const trimmed = value.trim()
    const id = trimmed === '' ? null : Number(trimmed)
    if (id !== null && (!Number.isInteger(id) || id <= 0)) { setMessage('ID utente non valido'); return }
    try {
      await partnersApi.linkUser(ledgerPartnerId, id)
      await onChanged()
      setMessage('Salvato')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Errore')
    }
  }

  return (
    <div className="d-flex align-items-end gap-1">
      <TextField label="Utente collegato (ID)" className="md-u-w-160" value={value} onChange={e => setValue(e.target.value)} placeholder="nessuno" />
      <Button size="sm" variant="outlined" onClick={() => void save()}>Collega</Button>
      {message && <span className="md-typescale-body-small text-muted ms-1">{message}</span>}
    </div>
  )
}

/* ── F-069: sezioni dell'amministrazione abilitate al socio ───────────────────────────────────── */

function DisclosureControl({ partnerId, current, onChanged }: {
  partnerId: number
  current: string
  onChanged: () => Promise<void>
}) {
  const parse = (v: string) => v.split(',').map(x => x.trim()).filter(Boolean)
  const [selected, setSelected] = useState<string[]>(parse(current))
  const [message, setMessage] = useState('')

  useEffect(() => { setSelected(parse(current)); setMessage('') }, [partnerId, current])

  function toggle(id: string) {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  async function save() {
    setMessage('')
    try {
      await partnersApi.setDisclosure(partnerId, selected)
      await onChanged()
      setMessage('Salvato')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Errore')
    }
  }

  return (
    <div className="d-flex align-items-end gap-2 flex-wrap">
      <div>
        <div className="md-typescale-body-small mb-1">Sezioni visibili al socio</div>
        <div className="d-flex gap-3">
          {PARTNER_SECTIONS.map(sec => (
            <Checkbox key={sec.id} label={sec.label} checked={selected.includes(sec.id)} onChange={() => toggle(sec.id)} />
          ))}
        </div>
      </div>
      <Button size="sm" variant="outlined" onClick={() => void save()}>Salva sezioni</Button>
      {message && <span className="md-typescale-body-small text-muted">{message}</span>}
    </div>
  )
}

/** Mostra soltanto le sezioni che il server ha incluso (quelle non abilitate non esistono nella risposta). */
function OverviewSections({ overview }: { overview: PartnerOverview }) {
  if (!overview.costs && overview.budget === undefined && !overview.outflows) return null
  return (
    <StatGrid>
      {overview.costs && (
        <SummaryCard label="Costi del mese" value={formatEur(overview.costs.month.total)} note={`${formatDate(overview.costs.month.from)} – ${formatDate(overview.costs.month.to)}`} />
      )}
      {overview.costs && (
        <SummaryCard label="Costi dell'anno" value={formatEur(overview.costs.year.total)} note={`${formatDate(overview.costs.year.from)} – ${formatDate(overview.costs.year.to)}`} />
      )}
      {overview.budget !== undefined && (
        overview.budget === null
          ? <SummaryCard label="Budget annuale" value="—" note="Nessun budget annuale totale impostato" />
          : <SummaryCard label={`Scostamento budget ${overview.budget.year}`} value={formatPct(overview.budget.variancePct)} note={`Consuntivo ${formatEur(overview.budget.actual)} su budget ${formatEur(overview.budget.budget)}`} />
      )}
      {overview.outflows && (
        <SummaryCard label={`Uscite a ${overview.outflows.days} giorni`} value={formatEur(overview.outflows.total)} note={`${overview.outflows.count} scadenze`} />
      )}
    </StatGrid>
  )
}

/* ── Modali ──────────────────────────────────────────────────────────────────────────────────── */

function Modal({ title, onClose, onSubmit, actions, supportingText, children }: { title: string; onClose: () => void; onSubmit: () => void; actions: ReactNode; supportingText?: ReactNode | undefined; children: ReactNode }) {
  return (
    <Dialog open onClose={onClose} title={title} supportingText={supportingText} actions={actions} as="form" onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
      {children}
    </Dialog>
  )
}

function parseAmount(s: string): number {
  return Number(s.replace(',', '.'))
}

function MovementModal({ partnerId, ledger, accounts, onClose, onSaved }: {
  partnerId: number
  ledger: PartnerLedger
  accounts: FinancialAccountOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<MovementForm>(EMPTY_MOVEMENT)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const categoryInfo = ledger.categories.find(c => c.category === form.category)
  const needsEffect = categoryInfo !== undefined && categoryInfo.fixedEffect === null

  function set<K extends keyof MovementForm>(k: K, v: string) { setForm(f => ({ ...f, [k]: v })) }

  async function save() {
    if (!form.category) { setError('La categoria è obbligatoria'); return }
    const amount = parseAmount(form.amount)
    if (!Number.isFinite(amount) || amount <= 0) { setError('Importo non valido (deve essere maggiore di zero)'); return }
    if (!form.description.trim()) { setError('La causale è obbligatoria'); return }
    if (needsEffect && form.effect === '') { setError('Per questa categoria indica se la società deve al socio (+) o il socio alla società (−)'); return }

    const body: PartnerMovementBody = {
      movementDate: form.movementDate,
      category: form.category,
      amount,
      description: form.description,
      effect: needsEffect ? Number(form.effect) : undefined,
      documentReference: form.documentReference || undefined,
      sourceAccountId: form.sourceAccountId ? Number(form.sourceAccountId) : undefined,
      destinationAccountId: form.destinationAccountId ? Number(form.destinationAccountId) : undefined,
      costCentre: form.costCentre || undefined,
      notes: form.notes || undefined,
    }
    setSaving(true)
    setError('')
    try {
      await partnersApi.createMovement(partnerId, body)
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Nuovo movimento" onClose={onClose} onSubmit={() => void save()} actions={<>
        <Button variant="text" onClick={onClose}>Annulla</Button>
        <Button type="submit" variant="text" disabled={saving}>Registra</Button>
      </>}>
        {error && <Callout tone="error">{error}</Callout>}
        <FormSection title="Movimento">
          <TextField label="Data" type="date" value={form.movementDate} onChange={e => set('movementDate', e.target.value)} />
          <Select label="Categoria" value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">Seleziona…</option>
            {ledger.categories.map(c => (
              <option key={c.category} value={c.category}>
                {c.label}{c.fixedEffect === null ? '' : c.fixedEffect > 0 ? '  (+)' : '  (−)'}
              </option>
            ))}
          </Select>
          {needsEffect && (
            <Select className="md-span-full" label="Effetto" value={form.effect} onChange={e => set('effect', e.target.value)}>
              <option value="">Seleziona…</option>
              <option value="1">+ La società deve al socio</option>
              <option value="-1">− Il socio deve alla società</option>
            </Select>
          )}
          <TextField label="Importo" inputMode="decimal" value={form.amount} onChange={e => set('amount', e.target.value)} />
          <TextField label="Riferimento documento" maxLength={200} value={form.documentReference} onChange={e => set('documentReference', e.target.value)} />
          <TextField className="md-span-full" label="Causale" value={form.description} onChange={e => set('description', e.target.value)} />
        </FormSection>
        <FormSection title="Imputazione">
          <AccountSelect label="Conto di origine" accounts={accounts} value={form.sourceAccountId} onChange={v => set('sourceAccountId', v)} />
          <AccountSelect label="Conto di destinazione" accounts={accounts} value={form.destinationAccountId} onChange={v => set('destinationAccountId', v)} />
          <TextField className="md-span-full" label="Centro di costo" maxLength={100} value={form.costCentre} onChange={e => set('costCentre', e.target.value)} />
        </FormSection>
        <FormGrid columns={1}>
          <TextField label="Note" multiline rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </FormGrid>
    </Modal>
  )
}

function AccountSelect({ label, accounts, value, onChange }: { label: string; accounts: FinancialAccountOption[]; value: string; onChange: (v: string) => void }) {
  return (
    <Select label={label} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Nessuno</option>
      {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
    </Select>
  )
}

function ExpensePaidModal({ partnerId, expenses, accounts, onClose, onSaved }: {
  partnerId: number
  expenses: Expense[]
  accounts: FinancialAccountOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const [expenseId, setExpenseId] = useState('')
  const [accountId, setAccountId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    if (!expenseId) { setError('Seleziona la spesa'); return }
    setSaving(true)
    setError('')
    try {
      await partnersApi.expensePaid(partnerId, {
        expenseId: Number(expenseId),
        sourceAccountId: accountId ? Number(accountId) : undefined,
      })
      onSaved()
    } catch (e) {
      // 404 spesa inesistente / 409 spesa già collegata a un movimento: messaggio del server.
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Spesa pagata dal socio" onClose={onClose} onSubmit={() => void save()}
      supportingText={<>Registra un anticipo del socio per l'intero totale della spesa: la società risulta debitrice verso il socio
          finché non lo rimborsa ("Compensa"). Lo stato della spesa non cambia.</>}
      actions={<>
        <Button variant="text" onClick={onClose}>Annulla</Button>
        <Button type="submit" variant="text" disabled={saving}>Registra</Button>
      </>}>
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid columns={1}>
          <Select label="Spesa" value={expenseId} onChange={e => setExpenseId(e.target.value)}>
            <option value="">Seleziona…</option>
            {expenses.map(x => (
              <option key={x.id} value={x.id}>
                {formatDate(x.expenseDate)} · {x.description} · {formatEur(x.totalAmount)}
              </option>
            ))}
          </Select>
          <AccountSelect label="Conto del socio usato" accounts={accounts} value={accountId} onChange={setAccountId} />
        </FormGrid>
    </Modal>
  )
}

function SettleModal({ partnerId, movement, accounts, onClose, onSaved }: {
  partnerId: number
  movement: PartnerLedgerMovement
  accounts: FinancialAccountOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const refund = movement.category === 'SpesaAnticipata'
  const [date, setDate] = useState(today())
  const [accountId, setAccountId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    if (!accountId) { setError('Seleziona il conto aziendale'); return }
    setSaving(true)
    setError('')
    try {
      await partnersApi.settle(partnerId, movement.id, { date, accountId: Number(accountId) })
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante la compensazione')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={refund ? 'Rimborsa la spesa al socio' : 'Il socio versa l\'incasso alla società'} onClose={onClose} onSubmit={() => void save()}
      supportingText={<>Compensa il movimento #{movement.id} ({formatEur(movement.amount)}) con un movimento opposto collegato: il saldo
          torna a zero per questa operazione.</>}
      actions={<>
        <Button variant="text" onClick={onClose}>Annulla</Button>
        <Button type="submit" variant="text" disabled={saving}>Compensa</Button>
      </>}>
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid columns={1}>
          <TextField label="Data" type="date" value={date} onChange={e => setDate(e.target.value)} />
          <AccountSelect label={refund ? 'Conto aziendale da cui si paga' : 'Conto aziendale che riceve'} accounts={accounts} value={accountId} onChange={setAccountId} />
        </FormGrid>
    </Modal>
  )
}

function ReverseModal({ partnerId, movement, onClose, onSaved }: {
  partnerId: number
  movement: PartnerLedgerMovement
  onClose: () => void
  onSaved: () => void
}) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    setSaving(true)
    setError('')
    try {
      await partnersApi.reverse(partnerId, movement.id, reason.trim() || undefined)
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante lo storno')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={`Storna il movimento #${movement.id}`} onClose={onClose} onSubmit={() => void save()}
      supportingText={<>Il movimento non si modifica né si cancella: viene registrato un movimento di segno opposto (categoria Rettifica).
          Uno storno non può a sua volta essere stornato.</>}
      actions={<>
        <Button variant="text" onClick={onClose}>Annulla</Button>
        <Button type="submit" variant="text" tone="error" disabled={saving}>Storna</Button>
      </>}>
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid columns={1}>
          <TextField label="Motivo" value={reason} onChange={e => setReason(e.target.value)} />
        </FormGrid>
    </Modal>
  )
}
